import { useCallback, useMemo, useState } from "react"
import { DateTime } from "luxon"
import { makeStyles } from "tss-react/mui"
import { UseQueryResult } from "@tanstack/react-query"
import { Grid, MenuItem, Typography } from "@mui/material"
import { faWallet } from "@fortawesome/pro-light-svg-icons"
import { MRT_ColumnDef, MRT_Row } from "material-react-table"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { useInitialDateFilters } from "../../../../../shared/components/table/useInitialDateFilters"
import { defaultFilterOptionsList, FiltersDrawerIdCards, FiltersDrawerIdCardsProps } from "./FiltersDrawerIdCards"
import { StandardTwoLinesCell } from "../../../../../shared/components/table/StandardTwoLinesCell"
import { StandardFilterButton } from "../../../../../shared/components/table/StandardFilterButton"
import { StandardExportButton } from "../../../../../shared/components/table/StandardExportButton"
import { StandardTableFilter } from "../../../../../shared/components/table/StandardTableFilters"
import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"
import { useApiQueryTable } from "../../../../../core/api/components/ApiQueryTable"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { fetchIdCards, FetchIdCardsResponse, MerchantSettingsIdCards } from "../idCardsApi"
import useActiveFiltersCount from "../hooks/useActiveFiltersCount"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import { useMerchant } from "../../../../merchants/merchantHooks"
import { IdCardsDetailsDialog } from "./IdCardsDetailsDialog"
import { useExportIdCards } from "../hooks/useExportIdCards"
import { CardType, MerchantIdCards } from "../idCardsTypes"
import { toDinero } from "../../../../currency/currencyLib"
import { useMoney } from "../../../../currency/useMoney"
import { filterDataByEndUser } from "../helpers"
import { StandardDateRangeFilter } from "../../../../../shared/components/table/StandardDateRangeFilter"

const useStyles = makeStyles()((theme) => ({
    iconWrap: {
        color: theme.palette.tailwind.slate[500],
    },
    icon: {
        color: "#0EA5E9",
        paddingRight: "0.5rem",
    },
}))

function transformData(data: FetchIdCardsResponse) {
    return data.userCards
}

export function IdCardsTable() {
    const { classes } = useStyles()
    const moneyFactory = useMoney({})
    const { l10n } = useAppLocalization()
    const { merchantId } = useMerchant()

    const [idCard, setIdCard] = useState<MerchantIdCards>()
    const [showDetailsDialog, setShowDetailsDialog] = useState(false)

    const [drawerOpen, setDrawerOpen] = useState(false)
    const { startDate, endDate } = useInitialDateFilters()
    const [dateState, setDateState] = useState<{
        startDate: DateTime
        endDate: DateTime
    }>({
        startDate: startDate,
        endDate: endDate,
    })
    const [filtersList, setFiltersList] = useState<FiltersDrawerIdCardsProps>({
        ...defaultFilterOptionsList,
    })

    const { activeCount } = useActiveFiltersCount(filtersList)

    const merchantIdCards = useApiQuery(fetchIdCards, {
        queryName: MerchantSettingsIdCards.ID_CARDS,
        dependencies: {
            merchantId,
            from: dateState.startDate.toISODate(),
            to: dateState.endDate.toISODate(),
            cardNumber: !!filtersList.cardNumber ? filtersList.cardNumber : undefined,
            isActive: filtersList.isInActive ? undefined : !filtersList.isInActive,
            customerId: !!filtersList.customerId ? filtersList.customerId : undefined,
            customerGroupId: !!filtersList.customerGroupId ? filtersList.customerGroupId : undefined,
        },
        select: transformData,
    })

    const tableData = useMemo(() => {
        return filterDataByEndUser(merchantIdCards.data, filtersList)
    }, [merchantIdCards.data, filtersList])

    const { ApiQueryTable, columnFactory } = useApiQueryTable({ ...merchantIdCards, data: tableData } as UseQueryResult<
        typeof tableData
    >)

    const columns = useMemo<MRT_ColumnDef<MerchantIdCards>[]>(
        () => [
            columnFactory.nameColumn({
                header: l10n.getString("merchant-id-cards-table-card-type"),
                accessorKey: "paymentCard",
                Cell: ({ row }) => (
                    <StandardTwoLinesCell
                        title={l10n.getStringForEnum(
                            "merchant-id-cards-table-card-type-select-item",
                            row.original.paymentCard.cardType,
                            row.original.paymentCard.cardType,
                        )}
                        subtitle1={row.original.paymentCard.name}
                    />
                ),
            }),
            columnFactory.nameColumn({
                header: l10n.getString("merchant-id-cards-table-card-number"),
                accessorKey: "paymentCard.cardNumber",
            }),
            columnFactory.nameColumn({
                header: l10n.getString("merchant-id-cards-table-user"),
                accessorKey: "user",
                Cell: ({ row }) => (
                    <StandardTwoLinesCell
                        title={
                            <Grid container gap="1rem">
                                {row.original.user.displayName}

                                {row.original.paymentCard.cardType === CardType.TOP_UP && (
                                    <Typography variant="caption" className={classes.iconWrap}>
                                        <FontAwesomeIcon icon={faWallet} className={classes.icon} />
                                        <span>{moneyFactory.format(toDinero(row.original.user.account.balance))}</span>
                                    </Typography>
                                )}
                            </Grid>
                        }
                        subtitle1={row.original.user.employeeId}
                        subtitle2={row.original.user.email}
                    />
                ),
            }),
            columnFactory.shortNameColumn({
                header: l10n.getString("merchant-id-cards-table-company"),
                accessorKey: "user.company.name",
            }),
            columnFactory.activeInactiveStatusColumn(
                {
                    header: l10n.getString("merchant-id-cards-table-status"),
                    accessorKey: "paymentCard.isActive",
                },
                (row) => row?.paymentCard.isActive,
            ),
        ],
        [l10n, columnFactory],
    )

    const renderRowActionMenuItems = useCallback(
        ({ row, closeMenu }: { row: MRT_Row<MerchantIdCards>; closeMenu: () => void }) => [
            <MenuItem
                key={`details-${row.original.paymentCard.id}`}
                onClick={() => {
                    setIdCard(row.original)
                    closeMenu()
                    setShowDetailsDialog(true)
                }}
            >
                <LocalizedStrict id="id-cars-table-button-details">
                    <span>Details</span>
                </LocalizedStrict>
            </MenuItem>,
        ],
        [],
    )

    const exportIdCards = useExportIdCards({
        data: merchantIdCards.data,
        startDate: dateState.startDate || DateTime.now(),
        endDate: dateState.endDate || DateTime.now(),
    })

    return (
        <>
            <ApiQueryTable
                columns={columns}
                initialState={{ density: "spacious" }}
                renderTopToolbarCustomActions={() => (
                    <StandardTableFilter variant="left">
                        <StandardDateRangeFilter value={dateState} onChange={setDateState} />
                    </StandardTableFilter>
                )}
                renderToolbarInternalActions={({ table }) => (
                    <StandardTableFilter variant="right" table={table}>
                        <StandardFilterButton onClick={() => setDrawerOpen(true)} activeCount={activeCount} />
                        <StandardExportButton onClick={() => exportIdCards()} />
                    </StandardTableFilter>
                )}
                renderRowActionMenuItems={renderRowActionMenuItems}
            />

            <IdCardsDetailsDialog
                idCard={idCard}
                open={showDetailsDialog}
                onClose={() => {
                    setIdCard(undefined)
                    setShowDetailsDialog(false)
                }}
            />

            <FiltersDrawerIdCards
                setFiltersList={setFiltersList}
                drawerOpen={drawerOpen}
                setDrawerOpen={setDrawerOpen}
                filtersList={filtersList}
            />
        </>
    )
}
